import { EStatusDoc, TQueryParamsGetData } from '@src/configs/interface.config'
import { TUser } from './user'
import { TItemCodeGroup } from './item-code-group'

// Enum cho loại chiến dịch khuyến mại
export enum EPromotionCampaignType {
  DISCOUNT = 'DISCOUNT', // Chiết khấu theo doanh số tháng
  REWARD = 'REWARD', // Thưởng theo sản lượng
  CUSTOM = 'CUSTOM', // Chiến dịch tùy chỉnh
}

// Enum cho đơn vị tính
export enum EPromotionUnit {
  SQM = 'SQM', // m²
  TON = 'TON', // tấn
  UNIT = 'UNIT', // cái
}

// Enum cho loại phần thưởng
export enum ERewardType {
  DISCOUNT = 'DISCOUNT', // Chiết khấu
  REWARD = 'REWARD', // Thưởng
}

// Mapping để hiển thị
export const promotionCampaignTypeLabels: Record<EPromotionCampaignType, string> = {
  [EPromotionCampaignType.DISCOUNT]: 'Chiết khấu theo doanh số tháng',
  [EPromotionCampaignType.REWARD]: 'Thưởng theo sản lượng',
  [EPromotionCampaignType.CUSTOM]: 'Chiến dịch tùy chỉnh',
}

export const promotionUnitLabels: Record<EPromotionUnit, string> = {
  [EPromotionUnit.SQM]: 'm²',
  [EPromotionUnit.TON]: 'tấn',
  [EPromotionUnit.UNIT]: 'cái',
}

export const statusLabels: Record<string, string> = {
  [EStatusDoc.DRAFT]: 'Nháp',
  [EStatusDoc.PUBLISHED]: 'Phát hành',
  [EStatusDoc.CANCELLED]: 'Hủy',
  [EStatusDoc.ARCHIVED]: 'Lưu trữ',
}

// Type cho chi tiết phần thưởng/chiết khấu của từng sản phẩm
export type TPromotionCampaignRuleReward = {
  id?: number
  ruleId?: number
  groupItemCode: string // Mã nhóm sản phẩm
  groupColorCode?: string // Mã màu nhóm sản phẩm (nếu có)
  groupBrandCode?: string // Mã thương hiệu nhóm sản phẩm (nếu có)
  productName: string // Tên sản phẩm
  discount?: number // Giá trị chiết khấu (VNĐ/m²)
  reward?: string // Phần thưởng (văn bản tự do)
  rewardNumber?: number // Giá trị phần thưởng (số tiền)
  createdAt?: Date
  updatedAt?: Date
}

// Type cho mốc thưởng/chiết khấu
export type TPromotionCampaignRule = {
  id?: number
  campaignId?: number
  level: number // Cấp độ mốc
  fromAmount?: number // Doanh số tối thiểu (VNĐ)
  toAmount?: number // Doanh số tối đa (VNĐ)
  volume?: number // Sản lượng yêu cầu
  unit?: EPromotionUnit // Đơn vị tính
  rewardType: ERewardType // Loại phần thưởng
  rewards: TPromotionCampaignRuleReward[] // Chi tiết phần thưởng theo sản phẩm
  createdAt?: Date
  updatedAt?: Date
}

// Type cho mốc thưởng điểm (chỉ áp dụng khi type=REWARD, ADR-030)
export type TPointMilestone = {
  id: number
  level: number
  pointsThreshold: number
  rewardAmountVnd: string // Raw VND, bigint-as-string
  note: string | null
  sortOrder: number
  createdAt?: Date
  updatedAt?: Date
}

export type TPointMilestoneInput = {
  level: number
  pointsThreshold: number
  rewardAmountVnd: string
  note?: string
  sortOrder: number
}

// Một dòng "Cách tích điểm": nhóm vật tư N ăn bao nhiêu điểm. Đơn vị KHÔNG nằm ở type này — do
// ItemCodeGroup.basis quyết định (ADR-039 §1).
export type TPointRateInput = {
  itemCodeGroupId: number
  unitQuantity: number // Vế trái "N <đơn vị> = M điểm". Trên REVENUE_VND đây CHÍNH LÀ mệnh giá VNĐ.
  points: number // Vế phải. Nhóm không tích điểm thì không khai dòng, không khai points=0.
  sortOrder: number
}

export type TItemCodeGroupRef = Pick<
  TItemCodeGroup,
  'id' | 'code' | 'displayName' | 'basis' | 'memberType' | 'memberCount'
>

export type TPointRateAdmin = TPointRateInput & {
  id: number
  itemCodeGroup: TItemCodeGroupRef
}

export type TOverlapWarningSample = {
  itemCode: string
  itemName: string
  groupIds: number[]
}

// BR-LOYAL-14 — cảnh báo mềm, không chặn ghi. samples giới hạn 20 dòng, total luôn chính xác.
export type TOverlapWarning = {
  total: number
  samples: TOverlapWarningSample[]
}

// Type cho đăng ký tham gia chiến dịch của đại lý
export type TAgentPromotionCampaignReg = {
  id?: number
  campaignId: number
  agentId: number
  fullName: string
  phoneNumber: string
  email: string
  companyName: string
  agentCode: string
  agreeTerms: boolean
  registrationDate?: Date
  createdAt?: Date
  updatedAt?: Date
}

// Type chính cho chiến dịch khuyến mại
export type TPromotionCampaign = {
  id?: number
  name: string // Tên chiến dịch
  type: EPromotionCampaignType // Loại chiến dịch
  description?: string // Mô tả chi tiết
  bannerUrl?: string // URL ảnh bìa
  startDate: Date // Ngày bắt đầu
  endDate: Date // Ngày kết thúc
  status: EStatusDoc // Trạng thái
  requireRegistration: boolean // Yêu cầu đăng ký tham gia
  noOverdueDebt: boolean // Yêu cầu không có công nợ quá hạn
  conditions?: string // Điều kiện tham gia
  rules?: TPromotionCampaignRule[] // Các mốc thưởng/chiết khấu
  earnRulesContent?: string | null // Richtext "Cách tích điểm" (chỉ hiển thị, type=REWARD)
  pointMilestones?: TPointMilestone[] // Lộ trình mốc thưởng điểm (type=REWARD)
  autoAccruePoints?: boolean // Công tắc bật/tắt tính điểm tự động (type=REWARD)
  pointRates?: TPointRateAdmin[] // Cấu hình engine GROUP_RATE
  // BR-LOYAL-10 tính ở BE. CMS disable lưới pointRates theo cờ này, không tự suy ra từ status.
  pointRatesEditable?: boolean
  overlapWarning?: TOverlapWarning // BR-LOYAL-14 — cảnh báo mềm, không chặn ghi
  registrations?: TAgentPromotionCampaignReg[] // Danh sách đăng ký
  author?: TUser // Người tạo
  editBy?: TUser // Người chỉnh sửa cuối
  createdAt?: Date
  updatedAt?: Date
  // Thông tin thống kê
  totalAgents?: number // Số lượng đại lý tham gia
  totalSales?: number // Tổng doanh số
  totalVolume?: number // Tổng sản lượng
  totalRewardCost?: number // Tổng chi phí thưởng/chiết khấu
}

// Type cho tạo mới chiến dịch
export type TPostPromotionCampaign = {
  name: string
  subName?: string // Tên phụ (nếu có)
  type: EPromotionCampaignType
  description?: string
  bannerUrl?: string
  startDate: Date
  endDate: Date
  requireRegistration?: boolean
  noOverdueDebt?: boolean
  conditions?: string
  benefit?: string // Lợi ích của chiến dịch
  rules: Omit<TPromotionCampaignRule, 'id' | 'campaignId' | 'createdAt' | 'updatedAt'>[]
  earnRulesContent?: string // Richtext "Cách tích điểm" — chỉ khi type=REWARD
  pointMilestones?: TPointMilestoneInput[] // Whole-array replace on update — bỏ field để giữ nguyên
  autoAccruePoints?: boolean
  // Whole-array replace, cùng ngữ nghĩa pointMilestones — bỏ field giữ nguyên mảng cũ, gửi [] xoá sạch.
  pointRates?: TPointRateInput[]
}

// Type cho cập nhật chiến dịch
export type TPatchPromotionCampaign = Partial<TPostPromotionCampaign> & {
  status?: EStatusDoc
}

// Type cho thay đổi trạng thái
export type TUpdatePromotionCampaignStatus = {
  status: EStatusDoc
  reason?: string // Lý do (dùng cho hủy chiến dịch)
}

// Type cho query parameters
export type TQueryPromotionCampaign = TQueryParamsGetData<{
  type?: EPromotionCampaignType
  status?: EStatusDoc
  startDate?: Date
  endDate?: Date
  name?: string
}>

// Type cho báo cáo chiến dịch
export type TPromotionCampaignReport = {
  campaignId: number
  campaignName: string
  totalAgents: number
  totalSales: number
  totalVolume: number
  totalRewardCost: number
  productGroups: {
    groupItemCode: string
    groupColorCode?: string // Mã màu nhóm sản phẩm (nếu có)
    groupBrandCode?: string // Mã thương hiệu nhóm sản phẩm (nếu có)
    productName: string
    sales: number
    volume: number
    rewardCost: number
  }[]
  timeline: {
    date: Date
    sales: number
    volume: number
    agents: number
  }[]
}

// Type cho sản phẩm áp dụng (dropdown options)
export type TProductGroup = {
  code: string
  name: string
  products: {
    code: string
    name: string
  }[]
}
